/**
 * AuthProvider — VarahiID sign-in state for Varahi MHI (2026-09-24).
 *
 * Caddy forward_auth guards the first visit and every API, KG and voice call.
 * It cannot guard a repeat visit served by the service worker (precached shell,
 * 3d-models cache), so this provider asks the gate on mount, every 5 minutes and
 * whenever the tab becomes visible. On a 401 it clears this app's caches and
 * sends the browser to the gate's login — at most once per 30 s per tab
 * (sessionStorage mhi_auto_sso_at, the guard Happen's LoginPage uses). If the
 * gate cannot be reached it changes nothing, so the offline demo still works.
 *
 * VITE_AUTH_MODE=off: renders its children and nothing else, no requests.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ACCESS_BASE, AUTH_ENABLED, SESSION_POLL_MS } from './config.js';
import { fetchSession, handleUnauthenticated, loginUrl, markGuard } from './session.js';
import { clearAppCaches, signOut as doSignOut } from './signOut.js';
import { AuthContext, OFF_AUTH } from './authContext.js';

// Tab switches can be frequent; the gate needs asking at most this often for them.
const VISIBILITY_MIN_GAP_MS = 15_000;

function safeSessionStorage() {
  try { return window.sessionStorage; } catch { return null; }
}

function LiveAuthProvider({ children }) {
  const [state, setState] = useState({ status: 'loading', user: null });
  const mounted = useRef(true);
  const inFlight = useRef(null);
  const lastCheck = useRef(0);

  const check = useCallback(() => {
    if (inFlight.current) return inFlight.current;
    lastCheck.current = Date.now();
    const run = (async () => {
      const { kind, user } = await fetchSession(window.fetch.bind(window), ACCESS_BASE);
      if (!mounted.current) return;
      if (kind === 'ok') {
        setState({ status: 'ok', user });
      } else if (kind === 'unauth') {
        setState({ status: 'unauth', user: null });
        await handleUnauthenticated({
          now: Date.now(),
          storage: safeSessionStorage(),
          location: window.location,
          accessBase: ACCESS_BASE,
          clearCaches: clearAppCaches,
          assign: (url) => window.location.assign(url),
        });
      } else {
        // Offline or gate unreachable: keep what we had.
        setState((s) => (s.status === 'loading' ? { status: 'unknown', user: null } : s));
      }
    })().finally(() => { inFlight.current = null; });
    inFlight.current = run;
    return run;
  }, []);

  useEffect(() => {
    mounted.current = true;
    check();
    const timer = setInterval(check, SESSION_POLL_MS);
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastCheck.current < VISIBILITY_MIN_GAP_MS) return;
      check();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', check);
    return () => {
      mounted.current = false;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', check);
    };
  }, [check]);

  const signIn = useCallback(() => {
    // A click is a person, not a loop: go now and restart the 30 s window.
    markGuard(safeSessionStorage(), Date.now());
    window.location.assign(loginUrl(ACCESS_BASE, window.location));
  }, []);

  const value = useMemo(() => ({
    status: state.status,
    user: state.user,
    perms: state.user ? state.user.perms : OFF_AUTH.perms,
    signOut: () => doSignOut(state.user),
    signIn,
    refresh: check,
  }), [state, signIn, check]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export default function AuthProvider({ children }) {
  if (!AUTH_ENABLED) return children;
  return <LiveAuthProvider>{children}</LiveAuthProvider>;
}
